
import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronUp, Calendar, ArrowRight } from "lucide-react";
import { BlogPost } from "@/types/blogPost";

interface Category {
  name: string;
  color: string;
  borderColor: string;
  id: string;
  color_hex: string;
  border_color_hex: string;
  text_color_hex: string;
}

interface CategoryGridProps {
  categories: Category[];
  filteredPosts: BlogPost[];
  onPostSelect: (post: BlogPost) => void;
}

export function CategoryGrid({ 
  categories, 
  filteredPosts, 
  onPostSelect 
}: CategoryGridProps) {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  // Ordenar categorias alfabeticamente
  const sortedCategories = [...categories].sort((a, b) => a.name.localeCompare(b.name));

  return (
    <section className="py-12 sm:py-16">
      <div className="container mx-auto px-4 sm:px-6">
        <div className="text-center mb-8 sm:mb-12">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-800 mb-4 font-primary">
            Categorias
          </h2>
          <p className="text-base sm:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto font-secondary">
            Explore nossos artigos organizados por categorias
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
          {sortedCategories.map((category) => {
            // Filtrar posts por categoria e ordenar por data decrescente
            const categoryPosts = filteredPosts
              .filter(post => {
                const postCategory = (post as any).blog_categories;
                return postCategory && postCategory.name === category.name;
              })
              .sort((a, b) => new Date(b.created_at || '').getTime() - new Date(a.created_at || '').getTime());

            return (
              <CategoryCard
                key={category.id}
                category={category}
                posts={categoryPosts}
                onPostSelect={onPostSelect}
                formatDate={formatDate}
              />
            );
          })}
        </div>
      </div>
    </section>
  );
}

// Componente separado para cada categoria com estado independente
function CategoryCard({ 
  category, 
  posts, 
  onPostSelect, 
  formatDate 
}: {
  category: Category;
  posts: BlogPost[];
  onPostSelect: (post: BlogPost) => void;
  formatDate: (dateString: string) => string;
}) {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <div className="space-y-3">
      {/* Título da categoria fora do componente */}
      <h3 
        className="text-xl font-semibold"
        style={{ color: category.text_color_hex }}
      >
        {category.name} ({posts.length})
      </h3>

      {/* Card da categoria */}
      <Card className="overflow-hidden">
        <CardContent className="p-0">
          <Button
            onClick={handleToggle}
            className="w-full p-4 sm:p-6 flex items-center justify-between text-left hover:bg-gray-50 bg-transparent text-black border-none rounded-none"
          >
            <span className="text-gray-600">
              {isExpanded ? 'Ocultar posts' : 'Ver posts'}
            </span>
            {isExpanded ? (
              <ChevronUp className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            )}
          </Button>

          {isExpanded && (
            <div className="border-t bg-gray-50 p-4 sm:p-6">
              {posts.length > 0 ? (
                <div className="h-80 overflow-y-auto space-y-4 pr-2">
                  {posts.map((post) => (
                    <article 
                      key={post.id}
                      className="bg-white p-4 rounded-lg shadow-sm border hover:shadow-md transition-shadow"
                    >
                      <div className="flex flex-col gap-3">
                        <div className="flex-1">
                          <h4 className="font-semibold text-lg mb-2 text-gray-800 line-clamp-2">
                            {post.title}
                          </h4>
                          
                          <div className="flex items-center text-gray-500 mb-3">
                            <Calendar className="w-4 h-4 mr-2" />
                            <span className="text-sm">
                              {post.created_at ? formatDate(post.created_at) : "Data não informada"}
                            </span>
                          </div>
                        </div>
                        
                        <Button 
                          onClick={() => onPostSelect(post)}
                          variant="outline" 
                          size="sm"
                          className="w-full"
                        >
                          Ler Mais
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                      </div>
                    </article>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <p>Nenhum post encontrado nesta categoria.</p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
